<template>
  <FlashNotifications ref="flash" />

  <div class="min-h-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- Compact object page header: icon/title row + facet tabs + search/filter toolbar -->
    <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6">

      <div class="flex items-center justify-between gap-3 h-14">
        <div class="flex items-center gap-2.5 min-w-0">
          <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"/>
            </svg>
          </div>
          <div class="min-w-0">
            <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none truncate">Applications</h1>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none truncate">{{ summaryLabel }}</p>
          </div>
        </div>
        <button type="button" @click="openAdd"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm flex-shrink-0">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
          </svg>
          <span class="hidden sm:inline">Add Application</span>
          <span class="sm:hidden">Add</span>
        </button>
      </div>

      <!-- Search + filter toolbar -->
      <div class="flex items-center gap-2 pb-3 flex-wrap">
        <div class="relative flex-1 min-w-[180px] max-w-sm">
          <svg class="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
          <input v-model="searchInput" type="text" placeholder="Search applications"
            class="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
        </div>

        <div class="relative app-filter-dropdown">
          <button type="button" @click="openDropdown = openDropdown === 'criticality' ? null : 'criticality'"
            :class="store.filters.criticality.length ? 'border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400 bg-blue-50/60 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            Criticality
            <span v-if="store.filters.criticality.length" class="text-xs font-semibold">({{ store.filters.criticality.length }})</span>
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
            </svg>
          </button>
          <div v-if="openDropdown === 'criticality'"
            class="absolute z-20 mt-1.5 w-44 bg-white dark:bg-[#1C2128] border border-gray-100 dark:border-gray-700 rounded-lg shadow-lg py-1.5">
            <label v-for="option in CRITICALITY_OPTIONS" :key="option.value"
              class="flex items-center gap-2.5 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer select-none">
              <input type="checkbox" :checked="store.filters.criticality.includes(option.value)" @change="store.toggleCriticality(option.value)"
                class="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0" />
              {{ option.label }}
            </label>
          </div>
        </div>

        <div class="relative app-filter-dropdown">
          <button type="button" @click="openDropdown = openDropdown === 'sort' ? null : 'sort'"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 7.5 7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5"/>
            </svg>
            {{ currentSortLabel }}
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
            </svg>
          </button>
          <div v-if="openDropdown === 'sort'"
            class="absolute z-20 mt-1.5 w-52 bg-white dark:bg-[#1C2128] border border-gray-100 dark:border-gray-700 rounded-lg shadow-lg py-1.5">
            <button v-for="option in SORT_OPTIONS" :key="option.value" type="button"
              @click="applySort(option)"
              :class="isActiveSort(option) ? 'text-blue-600 dark:text-blue-400 font-semibold' : 'text-gray-700 dark:text-gray-300'"
              class="w-full flex items-center justify-between gap-2.5 px-3 py-1.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-800">
              {{ option.label }}
              <svg v-if="isActiveSort(option)" class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
              </svg>
            </button>
          </div>
        </div>

        <button v-if="store.hasActiveFilters" type="button" @click="clearFilters"
          class="text-sm font-medium text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          Clear filters
        </button>
      </div>
    </div>

    <!-- Page content -->
    <div class="py-6 px-4 sm:px-6">

      <!-- Loading skeleton -->
      <div v-if="store.loading && store.applications.length === 0" class="animate-pulse bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden divide-y divide-gray-50 dark:divide-gray-800">
        <div v-for="i in 8" :key="i" class="flex items-center gap-3 px-4 py-3.5">
          <div class="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
          <div class="h-3 w-40 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="h-5 w-16 bg-gray-100 dark:bg-gray-800 rounded-full ml-auto"></div>
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="store.error" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center py-24 sm:py-32 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.error }}</p>
        <button @click="retry" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-sm transition-colors">
          Try again
        </button>
      </div>

      <!-- Empty state: no applications match the active filters -->
      <div v-else-if="store.applications.length === 0 && store.hasActiveFilters" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center py-20 sm:py-24 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">No matching applications</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">Try a different search term or clear your filters.</p>
        <button @click="clearFilters" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-sm transition-colors">
          Clear filters
        </button>
      </div>

      <!-- Empty state: no applications at all -->
      <div v-else-if="store.applications.length === 0" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center py-20 sm:py-24 text-center px-4">
        <div class="w-20 h-20 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-6">
          <svg class="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"/>
          </svg>
        </div>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">No applications yet</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400 max-w-xs mb-8">Add the software your organization builds or relies on to start tracking it here.</p>
        <button @click="openAdd" class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm">
          Add Application
        </button>
      </div>

      <!-- Applications list: single column, full-width rows separated by a single divider -->
      <div v-else class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <TransitionGroup name="app-row" tag="div" class="divide-y divide-gray-100 dark:divide-gray-800">
          <div v-for="application in store.applications" :key="application.id" @click="goToFactsheet(application)"
            class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 pl-4 pr-4 sm:pr-5 py-3.5 sm:py-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 active:bg-gray-200 dark:active:bg-gray-700 transition-colors">
            <div class="flex items-center gap-3 min-w-0 flex-1">
              <ItComponentLogo :name="application.name" :logo-url="application.logo_url" size="w-9 h-9" initial-text-size="text-xl" class="flex-shrink-0" />

              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2 flex-wrap">
                  <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{{ application.alias || application.name }}</h3>
                  <span v-if="application.alias" class="text-xs text-gray-400 dark:text-gray-500 truncate">({{ application.name }})</span>
                  <span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium" :class="criticalityBadgeClass(application.criticality)">
                    <CriticalityBars :criticality="application.criticality" />
                    {{ criticalityLabel(application.criticality) }}
                  </span>
                </div>
                <p v-if="application.description" class="text-xs text-gray-400 dark:text-gray-500 truncate mt-1">{{ application.description }}</p>
              </div>
            </div>

            <div class="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <p class="text-[11px] sm:text-xs text-gray-400 dark:text-gray-500 sm:text-right whitespace-nowrap">
                Added by <span class="font-medium text-gray-500 dark:text-gray-400">{{ application.added_by_name || 'Unknown' }}</span>
                <span class="hidden sm:inline"><br>{{ formatDate(application.created_at) }}</span>
                <span class="sm:hidden"> · {{ formatDate(application.created_at) }}</span>
              </p>

              <!-- Row actions — admins/the owner only, backend enforces the same. -->
              <div v-if="canEdit" class="relative row-menu" @click.stop>
                <button type="button" title="More actions" :disabled="duplicatingId === application.id"
                  @click="openRowMenuId = openRowMenuId === application.id ? null : application.id"
                  class="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-60">
                  <svg v-if="duplicatingId === application.id" class="w-4 h-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  <svg v-else class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 6a2 2 0 100-4 2 2 0 000 4zM12 14a2 2 0 100-4 2 2 0 000 4zM12 22a2 2 0 100-4 2 2 0 000 4z"/>
                  </svg>
                </button>
                <div v-if="openRowMenuId === application.id"
                  class="absolute right-0 z-20 mt-1 w-40 bg-white dark:bg-[#1C2128] border border-gray-100 dark:border-gray-700 rounded-lg shadow-lg py-1.5">
                  <button type="button" @click="handleDuplicate(application)"
                    class="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 text-left cursor-pointer">
                    <svg class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"/>
                    </svg>
                    Duplicate
                  </button>
                </div>
              </div>
            </div>
          </div>
        </TransitionGroup>
      </div>

      <!-- Infinite scroll sentinel -->
      <div ref="sentinel" class="h-8"></div>
      <div v-if="store.loadingMore" class="flex justify-center py-4">
        <div class="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    </div>
  </div>

  <!-- Add modal -->
  <AddApplicationModal v-if="modal.open"
    title="Add Application"
    submit-label="Add"
    :saving="modal.saving"
    :error="modal.error"
    @close="closeModal"
    @submit="submitModal" />
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { pageTitle } from 'utils/pageTitle.js'
import { useRouter } from 'vue-router'
import { orgStore } from 'controllers/vue_components/organizations/store'
import { useOrgApplicationsStore } from 'stores/orgApplications.js'
import { useGlobalApplicationsStore } from 'stores/globalApplications.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import AddApplicationModal from 'controllers/vue_components/organizations/applications/AddApplicationModal.vue'
import ItComponentLogo from 'controllers/vue_components/organizations/it_components/ItComponentLogo.vue'
import CriticalityBars from 'controllers/vue_components/organizations/applications/CriticalityBars.vue'
import { CRITICALITY_OPTIONS, SORT_OPTIONS, criticalityLabel, criticalityBadgeClass } from 'controllers/vue_components/organizations/applications/applicationOptions.js'

const flash = ref(null)
const router = useRouter()
const orgGlobalStore = orgStore()
const store = useOrgApplicationsStore()
const globalApplicationsStore = useGlobalApplicationsStore()

const org = computed(() => orgGlobalStore.current_org || {})

watch(() => org.value.name, (name) => {
  if (name) document.title = pageTitle('Applications', name)
}, { immediate: true })

const summaryLabel = computed(() => {
  const count = store.pageInfo.total_records
  return `${count} ${count === 1 ? 'application' : 'applications'}`
})

const currentSortLabel = computed(() => {
  const active = SORT_OPTIONS.find((option) => option.by === store.sort.by && option.order === store.sort.order)
  return active ? active.label : 'Sort'
})

function isActiveSort(option) {
  return option.by === store.sort.by && option.order === store.sort.order
}

async function applySort(option) {
  openDropdown.value = null
  await store.setSort(option.by, option.order)
}

// Admins/the owner only — same rule as the factsheet page's own canEdit,
// and the backend enforces it identically (duplicate is admin-gated there too).
const canEdit = computed(() => {
  const membership = orgGlobalStore.user_role_in_org?.users_organizations
  return membership?.role === 'admin' || membership?.org_owner === true
})

const modal = ref({ open: false, saving: false, error: '' })
const openDropdown = ref(null)
const openRowMenuId = ref(null)
const duplicatingId = ref(null)
const searchInput = ref('')
const sentinel = ref(null)
let searchDebounce = null
let observer = null

watch(searchInput, (value) => {
  clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => store.setSearch(value.trim()), 300)
})

function handleOutsideClick(e) {
  if (!e.target.closest('.app-filter-dropdown')) openDropdown.value = null
  if (!e.target.closest('.row-menu')) openRowMenuId.value = null
}

async function handleDuplicate(application) {
  openRowMenuId.value = null
  duplicatingId.value = application.id

  const res = await store.duplicateApplication(application.id)

  duplicatingId.value = null

  if (!res.js_error && res.response.success) {
    flash.value?.show('Application duplicated successfully.', 'notice')
  } else {
    flash.value?.show(actionErrorMessage(res), 'alert')
  }
}

onMounted(async () => {
  document.addEventListener('click', handleOutsideClick)
  await store.init(org.value.id)

  // Fire and forget: warms the autocomplete catalog used by the add modal
  // without blocking the page's own data load.
  globalApplicationsStore.fetchIfNeeded()

  observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) store.fetchMore()
  }, { threshold: 0.1 })
  if (sentinel.value) observer.observe(sentinel.value)
})

onUnmounted(() => {
  document.removeEventListener('click', handleOutsideClick)
  observer?.disconnect()
  clearTimeout(searchDebounce)
})

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

async function retry() {
  await store.fetchPage(true)
}

async function clearFilters() {
  searchInput.value = ''
  await store.clearFilters()
}

function openAdd() {
  modal.value = { open: true, saving: false, error: '' }
}

function goToFactsheet(application) {
  router.push(`/organizations/${org.value.url}/applications/${application.id}`)
}

function closeModal() {
  modal.value.open = false
}

// The API's `message` field is an internal identifier, not display text, so
// success/error copy shown to the user is built here instead.
function actionErrorMessage(res) {
  const validationErrors = res.error?.data?.data
  if (Array.isArray(validationErrors) && validationErrors.length) return validationErrors.join(', ')
  return 'Something went wrong. Please try again.'
}

async function submitModal(payload) {
  modal.value.saving = true
  modal.value.error = ''

  const res = await store.addApplication(payload)

  modal.value.saving = false

  if (!res.js_error && res.response.success) {
    closeModal()
    flash.value?.show('Application created successfully.', 'notice')
  } else {
    modal.value.error = actionErrorMessage(res)
  }
}
</script>

<style scoped>
/* No `position: absolute` on leave — search/filter changes swap the entire
   list on every keystroke, and absolute positioning on rows without an
   explicit positioning context made leaving and entering rows render on
   top of each other. A plain opacity fade avoids that without fighting
   the layout. */
.app-row-enter-active,
.app-row-leave-active {
  transition: opacity 0.15s ease;
}
.app-row-enter-from,
.app-row-leave-to {
  opacity: 0;
}

.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .animate-in { transition: transform 0.2s ease; }
.modal-enter-from .animate-in { transform: scale(0.96) translateY(8px); }
</style>
